/**
 * 자막 카드 — §14 "번역(대) + '들린 내용' 원문(소) + 배지".
 *
 * 참조 UI 의 정보 구조를 계승한다. 크기는 전부 토큰(--caption-*)이라
 * 발표 모드 스케일 오버라이드가 그대로 먹는다.
 */

import { useEffect, useRef, useState } from 'react'
import { Card, CardContent } from '@cupia/ui/card'
import { Badge } from '@cupia/ui/badge'
import { Button } from '@cupia/ui/button'
import { ChevronRight, Trash2 } from 'lucide-react'
import type {
  FeedItem,
  FinalItem,
  GapItem,
  ParagraphItem,
  PartialItem,
} from '../lib/reducer'
import type { CaptionGapData } from '../lib/events'
import {
  CutBadge,
  DirectionBadge,
  InferredBadge,
  ReviewBadge,
  SpeakerTurnMark,
  needsReview,
} from './badges'
import { finalSide, paragraphSide } from '../lib/align'
import { LevelWave } from './level-wave'
import { ApiError, translateSource, type XlateResult } from '../lib/api'
import { DEFAULT_ORDER, type FeedOrder } from '../lib/feed-order'

/**
 * 판정기 축으로 접힌 줄의 문구 — `badges.tsx` 의 `REASON_LABELS` **밖**에
 * 둔다. 그 표는 `tests/test_inventory.py` 가 지키는 계약이라 여기 문구를
 * 끼워 넣으면 배지 툴팁까지 같이 바뀐다.
 *
 * **단정형을 쓰지 않는다**("맥락 오류" 같은 문구 금지). 판정기 정확도는
 * 아직 단발 측정이고, 오판정이 나면 정상 발화에 오진 딱지가 박힌다 —
 * 배지축 FP 0(§1-2)이 무너지는 길이다.
 */
const ASSISTANT_COLLAPSED_LABEL = '통역기 답변으로 보입니다 — 눌러서 원문 확인'

/** 시각 표기 — 행 정렬의 눈금(split-rows.tsx)이 같은 표기를 재사용한다. */
export function clock(ts: number): string {
  return new Date(ts).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export interface CaptionCardProps {
  item: FeedItem
  /** 원문('들린 내용') 표시 여부 — 발표 모드에서는 접을 수 있다 */
  showSource?: boolean
  /**
   * 원문 가림(lib/source-veil.ts) — showSource 와 **다른 축**이다.
   * showSource=false 는 줄을 **제거**한다(발표 모드 — 큰 글씨에서 공간이
   * 아깝다). veilSource 는 줄을 남기고 **색만** 투명하게 한다: 상대측과
   * 화면을 같이 볼 때의 배려라, 토글 순간 카드 높이가 변해 화면이
   * 재배치되면 안 되고, 선택·복사·기록에는 원문이 그대로 남아야 한다.
   */
  veilSource?: boolean
  src: string
  dst: string
  /** 호스트만 전달 — 있으면 삭제 버튼이 뜬다(§8.4) */
  onDelete?: (item: FinalItem) => void
  /** §6.5-7 추론된 자막을 기본 한 줄로 접는다(펼치기 가능) — 원장은 그대로다. */
  collapseInferred?: boolean
  /**
   * 판정기(assistant_judge) 자막을 한 줄로 접는다 — **별도 축**이다.
   * `collapseInferred` 와 합치지 않는다: 합치면 한쪽을 끄려다 다른 쪽이
   * 같이 꺼지고, 접힌 줄을 보고 어느 축이 접었는지 못 가른다.
   */
  collapseAssistant?: boolean
  /**
   * 문단 카드 전용 — 이 문단에 통합된 원본 final 들(아코디언 내용).
   * 피드가 `consolidatedInto` 로 골라 넘긴다(카드가 전역 상태를 뒤지지
   * 않게 — 이 컴포넌트는 항목 하나만 아는 것이 계약이다).
   */
  members?: FinalItem[]
  /**
   * S25 원문 직역 온디맨드 — 둘 다 있어야 "원문 번역해보기" 버튼이 뜬다.
   * 둘 중 하나라도 없으면(구 호출부·목) 버튼 자체를 그리지 않는다 —
   * room/key 없이는 요청을 보낼 수 없으므로 죽은 버튼을 두지 않는다.
   */
  room?: string
  /** 뷰어·호스트 공용 — `auth.current()` 가 내주는 Bearer 자격(§5.2). */
  viewerKey?: string
  /**
   * 피드 정렬 방향(DEF-096) — 문단 카드의 멤버(아코디언) 표시 순서만 이
   * 값을 따른다. 리듀서·`indexMembers` 는 항상 오래된→최신으로 색인하므로
   * (`split-rows.ts:75`), 여기서 newest-first 면 뒤집어 최신→오래된으로
   * 그린다. 데이터는 건드리지 않는다 — **표시 순서만**.
   */
  order?: FeedOrder
}

export function CaptionCard({
  item,
  showSource = true,
  veilSource = false,
  src,
  dst,
  onDelete,
  collapseInferred = false,
  collapseAssistant = false,
  members,
  room,
  viewerKey,
  order = DEFAULT_ORDER,
}: CaptionCardProps): React.ReactElement {
  if (item.kind === 'gap') return <GapCard item={item} />
  if (item.kind === 'partial') return <PartialCard item={item} />
  if (item.kind === 'paragraph') {
    return (
      <ParagraphCard
        item={item}
        members={members ?? []}
        showSource={showSource}
        veilSource={veilSource}
        src={src}
        dst={dst}
        order={order}
        {...(onDelete ? { onDelete } : {})}
        {...(room !== undefined ? { room } : {})}
        {...(viewerKey !== undefined ? { viewerKey } : {})}
      />
    )
  }
  return (
    <FinalCard
      item={item}
      showSource={showSource}
      veilSource={veilSource}
      dst={dst}
      collapseInferred={collapseInferred}
      collapseAssistant={collapseAssistant}
      {...(onDelete ? { onDelete } : {})}
      {...(room !== undefined ? { room } : {})}
      {...(viewerKey !== undefined ? { viewerKey } : {})}
    />
  )
}

function FinalCard({
  item,
  showSource,
  veilSource,
  dst,
  onDelete,
  collapseInferred,
  collapseAssistant,
  room,
  viewerKey,
}: {
  item: FinalItem
  showSource: boolean
  veilSource: boolean
  dst: string
  onDelete?: (item: FinalItem) => void
  collapseInferred: boolean
  collapseAssistant: boolean
  room?: string
  viewerKey?: string
}): React.ReactElement {
  // §8.4 tombstone — 자리는 남기되 내용을 숨긴다(재작성 금지 원칙의 화면측 표현)
  if (item.deleted) {
    return (
      <Card data-testid="caption-tombstone" className="border-dashed opacity-60">
        {/* 자리만 지키는 카드다 — 내용이 한 줄뿐이라 여백까지 통역 카드만큼
            줄 이유가 없다. 전사 카드 쪽 배율(더 조밀)을 쓴다. */}
        <CardContent className="px-(--caption-pt-pad-x) py-(--caption-pt-pad-y)">
          <p className="caption-tombstone">삭제된 자막입니다.</p>
        </CardContent>
      </Card>
    )
  }

  const { data } = item
  const [expanded, setExpanded] = useState(false)
  /**
   * 펼치기·접기 사이의 **초점 인계**(교차리뷰 2026-08-10).
   *
   * 두 상태는 서로 다른 원소다 — 누른 버튼은 상태가 바뀌는 순간 언마운트되고,
   * 초점은 `document.body` 로 떨어진다. 키보드·스크린리더 사용자는 피드 안의
   * 자기 자리를 잃는다(다음 Tab 이 문서 맨 앞에서 다시 시작한다).
   * 그래서 새로 그려진 상대편 버튼에 초점을 **넘겨준다**. `pending` 은 다음
   * 렌더에서 한 번만 초점을 옮기기 위한 표식이다.
   */
  const [pendingFocus, setPendingFocus] = useState(false)
  /**
   * DEF-089 B안 — 2차 귀 원문 줄의 펼침 상태. 기본은 **접힘**이다.
   * `text_alt_hint` 가 true 여도 자동으로 펼치지 않는다: 문턱의 FP 에서
   * 맞는 원문 아래에 긴 날조 문장을 밀어넣게 된다. hint 는 어포던스 문구만
   * 바꾼다.
   */
  const [altExpanded, setAltExpanded] = useState(false)
  const collapsedRef = useRef<HTMLButtonElement>(null)
  const expandedRef = useRef<HTMLButtonElement>(null)
  // 판정기 축 — 서버가 실어 보낸 검토 사유에 `assistant_judge` 가 있는가.
  // `inferred` 를 재사용하지 않는다: 그 플래그는 표시 플래그가 아니라
  // 서버가 요약(main.py:678)·문맥(continuity.py:125)에서 제외하는 축이라,
  // 흘려 쓰면 접기를 켜지도 않은 방에서 발화가 조용히 사라진다(§1-1).
  const hasAssistantJudge = (data.review_reason ?? '')
    .split(',')
    .includes('assistant_judge')
  const collapsedByInferred = collapseInferred && data.inferred === true
  const collapsedByAssistant = collapseAssistant && hasAssistantJudge
  const isCollapsed = (collapsedByInferred || collapsedByAssistant) && !expanded
  // 접힌 줄은 **두 축을 구분해** 그린다 — 같은 문구를 쓰면 사후에 어느
  // 축이 접었는지 못 가른다. 판정기 문구는 **단정형이 아니다**: 판정
  // 정확도는 단발 측정이고(§2), 오판정이 정상 발화에 오진 딱지를 박으면
  // 배지축 FP 0(§1-2)이 무너진다.
  const collapsedKind: 'inferred' | 'assistant' = collapsedByInferred
    ? 'inferred'
    : 'assistant'

  useEffect(() => {
    if (!pendingFocus) return
    setPendingFocus(false)
    // 마우스로 눌렀을 때까지 초점 링이 남지는 않는다 — 브라우저가
    // :focus-visible 로 키보드 조작만 시각화한다.
    ;(isCollapsed ? collapsedRef : expandedRef).current?.focus()
  }, [pendingFocus, isCollapsed])

  // §6.5-7 유령 자막 접기 — 펼치기 전까지는 배지만 있는 한 줄로 줄인다.
  // 위에 SpeakerTurnMark 를 그리지 않는다: 접힌 줄 위에 매달린 구분선은
  // "여기서 바뀌었다" 가 아니라 접힌 카드 자체를 가리키는 것처럼 읽힌다.
  //
  // ■ 실화면 검토(2026-08-10 스크린샷)가 잡은 세 가지를 여기서 고친다:
  //   ① **눌러 보이지 않았다** — 점선 테두리에 본문 글씨뿐이라 비활성
  //      메타데이터로 읽혔다. 셰브런 아이콘 + hover 표면으로 조작 가능함을
  //      드러낸다(다른 곳의 Button 과 같은 언어).
  //   ② **행동 유도 문구가 화면에서 가장 작았다** — 정작 눌러야 할 줄인데
  //      시각·타임스탬프와 같은 크기였다. 본문 크기로 올린다.
  //   ③ **방향 칩이 없어 "자막이 깨진 것" 처럼 보였다** — 접힌 줄이 연달아
  //      셋이면 렌더링 오류로 읽힌다는 지적. 다른 카드와 같은 DirectionBadge
  //      를 달아 "이것도 하나의 발화" 임을 같은 문법으로 말한다.
  if (isCollapsed) {
    return (
      <button
        type="button"
        ref={collapsedRef}
        data-testid={
          collapsedKind === 'inferred'
            ? 'caption-inferred-collapsed'
            : 'caption-assistant-collapsed'
        }
        data-utt={item.utt}
        data-collapsed-by={collapsedKind}
        {...(collapsedKind === 'inferred' ? { 'data-inferred': 'true' } : {})}
        aria-expanded={false}
        aria-label={
          collapsedKind === 'inferred'
            ? '추론된 자막 펼치기'
            : '통역기 답변으로 보이는 자막 펼치기'
        }
        title={
          collapsedKind === 'inferred'
            ? '추론된(실제 발화가 아닐 수 있는) 자막입니다 — 눌러서 펼칩니다'
            : ASSISTANT_COLLAPSED_LABEL
        }
        onClick={() => {
          setExpanded(true)
          setPendingFocus(true)
        }}
        className="group/collapsed flex w-full cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-[var(--color-border)] px-2 py-1.5 text-start opacity-80 transition-colors hover:bg-[color-mix(in_oklab,var(--color-border)_35%,transparent)] hover:opacity-100 focus-visible:opacity-100"
      >
        <ChevronRight
          aria-hidden="true"
          className="h-4 w-4 shrink-0 opacity-70 transition-transform group-hover/collapsed:translate-x-0.5"
        />
        <span className="caption-meta caption-clock font-mono tabular-nums">{clock(item.ts)}</span>
        <DirectionBadge
          src={data.detected_lang ?? ''}
          dst={data.passthrough ? (data.detected_lang ?? '') : dst}
          passthrough={data.passthrough ?? false}
        />
        <InferredBadge data={data} />
        {/* caption-src 는 '들린 내용' 줄과 같은 크기다 — 메타(시각)보다 크고
            번역 본문보다 작아, 접힌 줄이 묻히지도 본문을 흉내내지도 않는다. */}
        <span className="caption-src">
          {collapsedKind === 'inferred' ? '펼쳐 보기' : ASSISTANT_COLLAPSED_LABEL}
        </span>
      </button>
    )
  }

  return (
    <>
      {/* 전환 표시는 카드 **바깥** 위에 온다 — 카드 안의 배지가 아니라 두
          발화 **사이**의 구분선이어야 "여기서 바뀌었다" 로 읽힌다. 정렬
          방향(최신 위/아래)과 무관하게 항상 해당 카드의 머리에 붙는다. */}
      <SpeakerTurnMark changed={data.spk_turn} />
    <Card
      data-testid="caption-final"
      data-utt={item.utt}
      // 카드 표면으로 종류를 구분한다(§14.1) — 긴 피드에서 텍스트 색만으로는
      // 한눈에 안 들어온다. needs_review 배지·tombstone·gap 은 그대로다.
      data-kind={data.passthrough ? 'passthrough' : 'interpreted'}
      // §6.5-7 추론 표시 — **표면까지 바꾼다.** 사용자가 배지와 함께 카드
      // 색을 지목한 이유가 있다("메시지 카드 색상이 바뀌거나 딱지가 붙어서
      // 나오지 않아"): 회의 중 프로젝터에 뜬 긴 피드를 훑을 때 작은 배지
      // 하나는 눈에 안 들어온다. 표면이 달라야 **읽기 전에** 걸러진다.
      //
      // 종류(passthrough/interpreted)와 **다른 속성**으로 둔다 — 겹치는
      // 축이라 data-kind 에 값을 하나 더 만들면 통역/전사 구분이 사라진다.
      data-inferred={data.inferred === true ? 'true' : undefined}
      // 표면 클래스는 **정렬 축**(S21-b — lib/align.ts 단일 판정)이다:
      // 통역문 = trans(좌·청록), 원어 그대로 = origin(우·파랑). 종류
      // (interpreted/passthrough)는 data-kind 가 계속 말한다 — 크기 규칙
      // ([data-kind='passthrough'])과 테스트가 그 축을 읽는다.
      className={`group caption-card-${finalSide(data)}${
        data.inferred === true ? ' caption-card-inferred' : ''
      }`}
    >
      {/* 여백은 **글자 크기에서 판다**(--caption-pad-*, lib/caption-scale.ts).
          고정 유틸리티(px-4 py-3)였을 때는 확대·축소 양쪽에서 어긋났다:
          키우면 글자에 비해 답답하고, 줄이면 여백만 남는다. 한 화면에 담기는
          발화 수가 곧 회의 맥락이라 여백은 그만큼의 맥락을 밀어내는 비용이다
          (2026-08-06 실사용: "높이가 너무 높다", "동적 크기 조정되게").

          전사 카드는 같은 규칙을 배율(0.7)만 달리해 쓴다 — 관계는
          PASSTHROUGH_PAD_RATIO 가 산술로 보장하므로 여기서 숫자를 다시 적지
          않는다(두 벌을 손으로 유지하면 한쪽만 고쳐 위계가 조용히 뒤집힌다).

          [주의] CardContent 의 기본값 `p-5`(와 space-y-4)는 tailwind-merge 가
          걷어내지 **못한다** — 방향 패딩(px·py)은 단축 패딩(p)과의 충돌로
          인식되지 않아 클래스 목록에 둘 다 남는다. 실제로 이기는 것은
          Tailwind v4 가 단축보다 방향 유틸리티를 **뒤에** 내보내기 때문이다
          (실측 확인). 그래서 상하·좌우를 항상 **둘 다** 지정해야 한다 —
          한쪽만 쓰면 나머지 축이 p-5(20px)로 남아 카드가 도로 커진다. */}
      <CardContent
        className={
          data.passthrough
            ? 'flex flex-col gap-(--caption-pt-gap) px-(--caption-pt-pad-x) py-(--caption-pt-pad-y)'
            : 'flex flex-col gap-(--caption-gap) px-(--caption-pad-x) py-(--caption-pad-y)'
        }
      >
        <div className="caption-meta-row flex flex-wrap items-center gap-1.5">
          <span className="caption-meta caption-clock font-mono tabular-nums">{clock(item.ts)}</span>
          <DirectionBadge
            src={data.detected_lang ?? ''}
            dst={data.passthrough ? (data.detected_lang ?? '') : dst}
            passthrough={data.passthrough ?? false}
          />
          {/* 추론 표시가 확인 필요보다 **앞**에 온다(§6.5-7): "이 내용을
              믿어도 되나" 가 "이걸 검토하라" 보다 먼저 읽혀야 한다. */}
          <InferredBadge data={data} />
          {/* 펼친 뒤 다시 접는 길 — collapseInferred 가 켜져 있고 실제로
              접혔었던(유령) 카드에만 붙는다. 다른 모든 카드는 손대지 않는다. */}
          {(collapsedByInferred || collapsedByAssistant) && (
            <button
              type="button"
              ref={expandedRef}
              aria-expanded={true}
              aria-label={
                collapsedKind === 'inferred'
                  ? '추론된 자막 접기'
                  : '통역기 답변으로 보이는 자막 접기'
              }
              title={
                collapsedKind === 'inferred'
                  ? '추론된(실제 발화가 아닐 수 있는) 자막을 한 줄로 접습니다'
                  : '통역기 답변으로 보이는 자막을 한 줄로 접습니다'
              }
              onClick={() => {
                setExpanded(false)
                setPendingFocus(true)
              }}
              className="caption-meta underline decoration-dotted"
            >
              접기
            </button>
          )}
          <ReviewBadge data={data} />
          <CutBadge data={data} />
          {data.attempt > 1 && (
            <Badge variant="softGray" title="재시도 후 확정된 발화(§7.4)">
              재시도 {data.attempt}
            </Badge>
          )}
          {onDelete && (
            /* 삭제 버튼이 **카드 높이를 지배하던 자리**다(실측): size="sm" 은
               --height-btn-sm(2rem=32px) 고정이라 글자 크기와 무관하게 배지줄
               높이를 32px 로 못박았다. 0단계에서 카드 78px 중 32px(41%),
               기본 2단계에서도 97px 중 32px(33%)이 이 한 줄이었다 — 여백을
               아무리 조여도 이걸 두면 카드는 얇아지지 않는다.

               높이 고정을 풀되(h-auto) **클릭 영역은 남기는** 방법으로
               패딩 + 음수 마진을 쓴다. 실제 치수:
                 · 누를 수 있는 상자 = 아이콘 14px + p-1.5(6px×2) = **26px**
                 · 배지줄에 더하는 높이 = 26 − (-my-1 이 상쇄하는 8px) = 18px
               음수 마진이 줄이는 것은 **레이아웃 상자**뿐이라 눌리는 넓이는
               그대로다. 버튼을 실제로 작게 만들면(size="xs") 상자 자체가
               24px 로 줄어 손가락으로 겨냥하기 어려워진다.

               [남은 한계] 26px 는 WCAG 2.2 최소 타깃(24×24, AA)은 넘지만
               권장치(44×44)에는 못 미친다. 이 버튼은 **호스트 전용**이고
               hover 로만 드러나는 보조 동작이라 마우스 조작이 전제다 —
               터치 우선 화면에 이 버튼을 내보낼 일이 생기면 그때는 크기를
               다시 봐야 한다(§8.4). */
            <Button
              variant="ghost"
              size="sm"
              aria-label="이 자막 삭제"
              title="이 자막 삭제 (§8.4 — 기록에 tombstone 이 남습니다)"
              className="ms-auto -my-1 h-auto p-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              onClick={() => onDelete(item)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* 번역(대) — 화면의 주인공 */}
        <p className="caption-ko">{data.ko}</p>

        {/* 원문(소) — '들린 내용'.
            가림(veilSource)은 **색만** 투명하게 한다: 텍스트는 DOM 에 남아
            선택·복사·스크린리더가 평소와 같고, 높이가 그대로라 토글해도
            화면이 재배치되지 않는다(lib/source-veil.ts 의 근거).
            passthrough 카드는 서버가 text 를 비워 보내므로(§6.5) 애초에
            이 줄이 없다 — 가림의 영향도 받지 않는다. */}
        {showSource && data.text && !data.text_raw && (
          <p
            data-testid="caption-src"
            data-veiled={veilSource || undefined}
            className={veilSource ? 'caption-src caption-src-veiled' : 'caption-src'}
          >
            <span className="me-1.5 opacity-70">들린 내용:</span>
            {data.text}
          </p>
        )}

        {/* text_raw 2단 표시(DEF-012 T3) — 게이트가 원전사를 정리한 경우
            정리 전/후를 모두 보여준다. text 가 빈 문자열(passthrough)이면
            "복원본" 줄은 생략하고 원전사 줄만 보여준다(TBD#1). */}
        {showSource && data.text_raw && (
          <>
            <p
              data-testid="caption-src-raw"
              data-veiled={veilSource || undefined}
              className={veilSource ? 'caption-src caption-src-veiled' : 'caption-src'}
            >
              <span className="me-1.5 opacity-70">
                들린 내용(원전사){data.text_raw_truncated ? ' (일부 생략)' : ''}:
              </span>
              {data.text_raw}
            </p>
            {data.text && (
              <p
                data-testid="caption-src-restored"
                data-veiled={veilSource || undefined}
                className={veilSource ? 'caption-src caption-src-veiled' : 'caption-src'}
              >
                <span className="me-1.5 opacity-70">복원본:</span>
                {data.text}
              </p>
            )}
          </>
        )}

        {/* DEF-089 B안 — 2차 귀 원문 병기. 기존 세 줄의 조건식은 건드리지
            않는다: 이 줄은 `caption-src` 와도 `caption-src-raw` 2단과도
            **공존**한다(둘 다 1차 귀의 말이고, 이것은 다른 귀의 말이다).
            `showSource` 축을 같이 타므로 발표 모드에서 원문을 지우면 이 줄도
            같이 사라지고, `veilSource` 도 같은 방식(색만 투명, DOM 유지)으로
            적용한다. */}
        {showSource && data.text_alt && (
          <div
            data-testid="caption-src-alt"
            data-veiled={veilSource || undefined}
            className={veilSource ? 'caption-src caption-src-veiled' : 'caption-src'}
          >
            <button
              type="button"
              /* hint 는 **표시만** 바꾼다(존재 여부가 아니다). true 면
                 어포던스를 흐리지 않아 눈에 띄고, false·부재면 다른 보조
                 문구와 같은 밝기로 남는다 — 배지축이 아니다. */
              className={
                data.text_alt_hint === true
                  ? 'inline-flex items-center gap-1 text-start'
                  : 'inline-flex items-center gap-1 text-start opacity-70 hover:opacity-100'
              }
              aria-expanded={altExpanded}
              onClick={() => setAltExpanded(v => !v)}
            >
              <ChevronRight
                className={altExpanded ? 'h-3 w-3 rotate-90' : 'h-3 w-3'}
              />
              {data.text_alt_source
                ? `다른 귀에는 이렇게 들렸습니다 (${data.text_alt_source})`
                : '다른 귀에는 이렇게 들렸습니다'}
            </button>
            {altExpanded && (
              <p data-testid="caption-src-alt-body">{data.text_alt}</p>
            )}
          </div>
        )}

        {/* S25 원문 직역 온디맨드 — "확인 필요"(§6.5)·"추론됨"(§6.5-7) 카드의
            원문을 그 자리에서 번역해 본다. 두 축 중 하나라도 걸리면 뜬다:
            지어낸 문장일수록, 검토가 필요할수록 "그럼 원문은 뭐라고
            했나"가 궁금해지는 지점이 정확히 여기다. */}
        {room !== undefined && viewerKey !== undefined && !!item.utt && !item.prev
          && !!data.text && (needsReview(data) || data.inferred === true) && (
          <XlateButton room={room} viewerKey={viewerKey} utt={item.utt} />
        )}
      </CardContent>
    </Card>
    </>
  )
}

/* ── S25 원문 직역 온디맨드 ────────────────────────────────────────────── */

/**
 * 모듈 전역 캐시 — 같은 utt 를 다시 눌러도 **재요청하지 않는다**(레이트
 * 리밋 6/분을 재렌더·재마운트가 갉아먹지 않게). 컴포넌트 상태가 아니라
 * 모듈 상태인 이유: 카드가 가상 스크롤로 마운트·언마운트를 반복해도
 * (TanStack Virtual) 캐시가 살아 있어야 스크롤로 사라졌다 돌아온 카드가
 * 또 호출을 쏘지 않는다.
 */
const xlateCache = new Map<string, XlateResult>()

type XlatePhase = 'idle' | 'loading' | 'done' | 'error'

function XlateButton({
  room,
  viewerKey,
  utt,
}: {
  room: string
  viewerKey: string
  utt: string
}): React.ReactElement {
  const cached = xlateCache.get(utt)
  const [phase, setPhase] = useState<XlatePhase>(cached ? 'done' : 'idle')
  const [result, setResult] = useState<XlateResult | undefined>(cached)
  const [errorMessage, setErrorMessage] = useState('')

  const run = (): void => {
    const hit = xlateCache.get(utt)
    if (hit) {
      setResult(hit)
      setPhase('done')
      return
    }
    setPhase('loading')
    setErrorMessage('')
    translateSource(room, viewerKey, utt)
      .then((res) => {
        xlateCache.set(utt, res)
        setResult(res)
        setPhase('done')
      })
      .catch((err: unknown) => {
        const message = err instanceof ApiError ? err.message : '번역에 실패했습니다.'
        setErrorMessage(message)
        setPhase('error')
      })
  }

  if (phase === 'done' && result) {
    return (
      <div className="caption-xlate-result" data-testid="caption-xlate">
        <div className="caption-xlate-result-head">
          <span className="caption-xlate-label">참고용 직번역 · 검증 안 됨</span>
          <DirectionBadge src={result.src_lang} dst={result.dst_lang} />
        </div>
        <p className="caption-xlate-text">{result.text}</p>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="caption-xlate-error" data-testid="caption-xlate">
        <span>{errorMessage}</span>
        <button
          type="button"
          className="caption-xlate-retry"
          onClick={run}
        >
          다시 시도
        </button>
      </div>
    )
  }

  return (
    <button
      type="button"
      className="caption-xlate-btn"
      data-testid="caption-xlate-btn"
      data-state={phase}
      disabled={phase === 'loading'}
      onClick={run}
    >
      {phase === 'loading' && <span className="caption-xlate-spinner" aria-hidden />}
      {phase === 'loading' ? '번역하는 중…' : '원문 번역해보기'}
    </button>
  )
}

/**
 * 장문 기록 문단 카드 — 라이브 카드들이 창 확정 후 "숙성"돼 접힌 자리.
 *
 * 정보 구조(목업 mock_unified.html 계승, 토큰은 이 저장소 것):
 *   메타(시간 범위 · N초 창 · 방향) → 문단 본문(대) → `원본 N건` 아코디언.
 * 아코디언 안에는 통합된 원본 카드가 **그대로**(원문·배지 보존) 들어간다 —
 * 회의록과 검증 원자료가 한 화면이다. 구성 카드에 확인 필요 배지가 하나라도
 * 있으면 문단 머리에도 배지를 올린다(접혀서 경고가 숨으면 안 된다).
 */
function ParagraphCard({
  item,
  members,
  showSource,
  veilSource,
  src,
  dst,
  onDelete,
  room,
  viewerKey,
  order = DEFAULT_ORDER,
}: {
  item: ParagraphItem
  members: FinalItem[]
  showSource: boolean
  veilSource: boolean
  src: string
  dst: string
  onDelete?: (item: FinalItem) => void
  room?: string
  viewerKey?: string
  order?: FeedOrder
}): React.ReactElement {
  const { data } = item
  const durS = Math.max(0, Math.round(data.dur_s))
  // `members` 는 `indexMembers`(split-rows.ts:75)가 항상 오래된→최신으로
  // 넘긴다(rows 모드의 좌측 카드 계약, D2-2). newest-first 피드에서는 그
  // 반대(최신→오래된)로 보여야 위 카드들의 내림차순과 어긋나지 않는다
  // (DEF-096) — 데이터는 그대로 두고 렌더 배열만 뒤집는다.
  const displayMembers = order === 'newest-first' ? [...members].reverse() : members
  const memberReview = members.some((m) => !m.deleted && needsReview(m.data))
  const flagged = data.uncertain || memberReview
  const side = paragraphSide(data, dst)
  return (
    <Card
      data-testid="caption-paragraph"
      data-para-id={item.id}
      data-kind="paragraph"
      // 표면 클래스는 실시간 카드와 **같은 정렬 축**(S21-b)이다 — 원어
      // 창의 문단과 통역 창의 문단이 색으로도 갈린다(사용자 불만: "ko 와
      // vi→ko 긴호흡 카드가 똑같이 보인다").
      className={`caption-card-${side}`}
    >
      <CardContent className="flex flex-col gap-(--caption-gap) px-(--caption-pad-x) py-(--caption-pad-y)">
        <div className="caption-meta-row flex flex-wrap items-center gap-1.5">
          <span className="caption-meta caption-clock font-mono tabular-nums">
            {clock(item.ts)}
          </span>
          <span className="caption-meta tabular-nums">{durS}초 창</span>
          {/* 방향 배지는 자막 카드와 같은 문법 — 문단의 번역문은 항상
              한국어(RECORD_SYSTEM 계약)라 dst 는 ko 로 고정된다. 원어
              창(side=origin)은 passthrough 표기(언어 하나)로 그린다. */}
          <DirectionBadge
            src={data.lang || src}
            dst={side === 'origin' ? data.lang : dst || 'ko'}
            passthrough={side === 'origin'}
          />
          {/* 긴호흡 라벨 — 실시간 카드와 같은 축(색·정렬)을 쓰므로, 창
              단위 통합 문단이라는 정체는 이 칩이 말한다(목업 .chip.lf). */}
          <span className="caption-chip-longform">긴호흡</span>
          {flagged && (
            <Badge
              variant="warning"
              title={
                data.uncertain
                  ? '복원 모델이 확신하지 못한 문단입니다'
                  : '원본 카드에 확인 필요 표시가 있습니다'
              }
            >
              확인 필요
            </Badge>
          )}
        </div>

        {/* 문단 본문(대) — 이 카드의 주인공. */}
        <p className="caption-ko">{data.ko}</p>

        {members.length > 0 && (
          <details className="caption-paragraph-details" data-testid="paragraph-raw">
            {/* 필 버튼 스타일 + 회전 마커(::before)는 CSS 가 그린다 —
                눌러 보이는 것이 요점이다(S21-b 지시 5). */}
            <summary>원본 {members.length}건 · 실시간 카드 펼치기</summary>
            {displayMembers.map((m) => (
              <div key={m.id} className="caption-paragraph-raw">
                <CaptionCard
                  item={m}
                  showSource={showSource}
                  veilSource={veilSource}
                  src={src}
                  dst={dst}
                  {...(onDelete ? { onDelete } : {})}
                  {...(room !== undefined ? { room } : {})}
                  {...(viewerKey !== undefined ? { viewerKey } : {})}
                />
              </div>
            ))}
          </details>
        )}
      </CardContent>
    </Card>
  )
}

/** 발화 순번 — id 는 서버가 붙인 `u-N`(§6.6 원장 순번)이다. 그 밖의 형태면
 *  숫자를 지어내지 않고 그리지 않는다. */
function uttOrdinal(utt: string): number | null {
  const m = /^u-(\d+)$/.exec(utt)
  return m ? Number(m[1]) : null
}

/** 서버 실측 음량이 있으면 파형을, 없으면(구 서버) 기존 막대를 그린다. */
function LevelWaveOrBar({ item }: { item: PartialItem }): React.ReactElement {
  if (typeof item.levelDbfs === 'number') {
    return <LevelWave levelDbfs={item.levelDbfs} utt={item.utt} />
  }
  return <span className="caption-pending-bar" aria-hidden />
}

function PartialCard({ item }: { item: PartialItem }): React.ReactElement {
  // 진행 숫자는 **서버 실측치만** 그린다(utt.state dur_ms). 프레임이 실제로
  // 도착해야 자라는 값이라, 이 숫자가 구르면 "입력→서버→VAD" 경로 전체가
  // 살아 있다는 증거다 — 클라이언트 시계로 세면 마이크가 죽어도 굴러가는
  // 거짓 진행이 된다(레벨 미터 실사고의 재림). 없으면(구 서버·partial 선착)
  // 숫자 없이 상태만 말한다.
  const durS = item.durMs === null ? null : Math.floor(item.durMs / 1000)
  const ordinal = uttOrdinal(item.utt)
  return (
    <Card
      data-testid="caption-partial"
      data-utt={item.utt}
      data-phase={item.phase}
      data-dur-ms={item.durMs ?? undefined}
      // 진행 중임을 **표면으로** 말한다(§14.1): 점선 테두리 + 낮은 채도.
      // 완성된 자막과 헷갈리면 안 되므로 확정 카드의 실선 액센트를 쓰지 않는다.
      className="caption-card-pending"
    >
      {/* 확정 카드와 **같은 여백**을 쓴다. 진행 중 카드는 확정되는 순간
          같은 자리에서 내용만 바뀌므로(§8.2 in-place 교체), 여백이 다르면
          확정될 때마다 카드 높이가 튀어 피드 전체가 재배치된다. */}
      <CardContent className="flex flex-col gap-(--caption-gap) px-(--caption-pad-x) py-(--caption-pad-y)">
        <div className="flex items-center gap-1.5">
          <span className="caption-meta caption-clock font-mono tabular-nums">{clock(item.ts)}</span>
          {/* 헤더 칩과 **같은 표기**("VI 듣는중")로 일관성을 유지한다.
              초 수는 tabular-nums 로 폭이 고정돼 1s 갱신에도 칩이 안 튄다. */}
          <Badge variant="soft" className="gap-1.5 tabular-nums">
            <span className="caption-pending-dot" aria-hidden />
            {item.phase === 'listening'
              ? `${(item.src || '').toUpperCase()} 듣는중`.trim() +
                (durS === null ? '' : ` · ${durS}초`)
              : '통역 중' + (durS === null ? '' : ` · ${durS}초 분량`)}
          </Badge>
          {/* 회의 내내 순번이 오르는 것 자체가 "돌고 있다" 는 증거다(§14) */}
          {ordinal !== null && (
            <span className="caption-meta tabular-nums">{ordinal}번째 발화</span>
          )}
        </div>

        {item.ko ? (
          /* in-place 교체 대상 — 리스트 재정렬 없이 이 텍스트만 자란다(§8.2) */
          <p className="caption-ko caption-partial">{item.ko}</p>
        ) : (
          /* 아직 텍스트가 없다 — 자리를 잡아 두되 내용을 지어내지 않는다.
             **서버가 실측한 음량**이 오면 파형으로, 안 오면(구 서버) 은은한
             막대로. 파형이 움직이는 것 자체가 "소리가 서버에 닿고 있다" 는
             증거다 — 클라이언트 자체 계산 레벨은 마이크가 죽어도 춤춘다. */
          <LevelWaveOrBar item={item} />
        )}
        {/* 스크린리더용 상태 안내 — 시각 효과는 읽히지 않는다 */}
        <span className="sr-only" aria-live="polite">
          {item.phase === 'listening' ? '발화를 듣는 중입니다' : '통역 중입니다'}
        </span>
      </CardContent>
    </Card>
  )
}

/** D3 — client_gap 로컬 사유의 사람이 읽을 이름(툴팁 한정). */
const CLIENT_GAP_REASON: Record<'backlog' | 'stall_evict', string> = {
  backlog: '적체 드롭',
  stall_evict: '보류 초과 폐기',
}

/**
 * D3 — 갭 카드 **툴팁** 문자열. 본문 문구와는 다른 축이다.
 *
 * 본문(`자막 누락 N초 · {reason}`)은 **불변**이다(§1-2 배지 문구 규율):
 * 배지 본문이 상세해지면 정상 회의에서도 눈에 띄는 잡음이 된다. 상세는
 * 호스트가 커서를 올려야 보이는 툴팁으로만 나간다.
 */
export function gapTooltip(data: CaptionGapData): string {
  const parts = [gapReasonLabel(data.reason)]
  if (data.frames_lost !== undefined) parts.push(`${data.frames_lost}프레임`)
  if (data.client_reason !== undefined) {
    parts.push(CLIENT_GAP_REASON[data.client_reason])
  }
  return parts.join(' · ')
}

/** 갭 사유의 사람이 읽을 이름 — 본문·툴팁이 **같은 출처**를 본다. */
function gapReasonLabel(reason: CaptionGapData['reason']): string {
  return reason === 'drop'
      ? '서버 적체로 드롭된 구간'
      : reason === 'verify_drop'
        // verify 가 오염(반복·페르소나 등)으로 판정해 버린 구간이다. L3 적체
        // 드롭과 원인이 달라 라벨을 분리한다(S24) — 적체로 오인하면 진단이
        // 엉뚱한 곳(서버 부하)으로 샌다.
        ? '오염 판정으로 제외된 구간'
        : reason === 'skip_unverified'
        // 전송은 정상이었다 — 모델이 통역 대상이 아니라고 판정했는데 서버가
        // 잰 길이·음량이 그 판정과 어긋난 구간이다. '전송 유실' 로 뭉뚱그리면
        // 원인을 엉뚱한 곳(네트워크)에서 찾게 된다.
        ? '통역되지 않은 발화 구간'
        : reason === 'gate_rejected'
        ? '비발화로 판정되어 통역하지 않은 구간'
        : '클라이언트 전송 유실 구간'
}

/**
 * §7.3/§8.2 유실 구간 — **반드시 보이게** 렌더한다.
 * "조용한 유실 금지"(§1 원칙 3)의 화면측 계약이다.
 */
function GapCard({ item }: { item: GapItem }): React.ReactElement {
  const seconds = Math.max(0, Math.round((item.data.to_ts - item.data.from_ts) / 1000))
  const reason = gapReasonLabel(item.data.reason)

  return (
    <div
      data-testid="caption-gap"
      role="status"
      className="flex items-center gap-3 px-2 py-1.5"
    >
      <span className="h-px flex-1 bg-[var(--color-warning)]/40" aria-hidden />
      <Badge variant="softWarn" title={gapTooltip(item.data)}>
        자막 누락 {seconds > 0 ? `${seconds}초` : ''} · {reason}
      </Badge>
      <span className="h-px flex-1 bg-[var(--color-warning)]/40" aria-hidden />
    </div>
  )
}
